import bsn from 'libraries/bootstrap-native.js';
import cookies from 'cookies.js';
import {format, setHours, setMinutes, setSeconds} from 'date-fns';
import Deferred from 'deferred.js';
import events from 'events.js';
import http from 'http.js';
import ko from 'knockout';
import {loadData, monitorArray} from 'ko/utils.js';
import {page} from 'page.js';
import {localStorageSetItem, localStorageGetItem} from 'core/utils.js';

import 'common/revertable.js';
import 'ko/bindings/notification_element.js';
import 'knockout.validation';
import 'ko/bindings/check_list.js';
import 'ko/elements/autocomplete.js';
import 'ko/extenders.js';
import 'ko/validators.js';

const days = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
};

const hours = [];
for (let i = 0; i < 24; i++) {
    const dt = new Date();
    hours.push(setSeconds(setMinutes(setHours(dt, i), 0), 0));
    hours.push(setSeconds(setMinutes(setHours(dt, i), 30), 0));
}

const openStates = [
    {value: true, text: 'Open'},
    {value: false, text: 'Closed'},
];

const addToLocalStorage = function (additionalProp) {
    let details = localStorageGetItem('intake-practice-details');
    details = {
        ...JSON.parse(details),
        ...additionalProp,
    };
    localStorageSetItem('intake-practice-details', JSON.stringify(details));
};

class PracticeDayHours {
    constructor(day) {
        this.day = ko.observable(day);
        this.openStates = ko.observable(openStates);
        this.openState = ko.observable(openStates[1]).revertable();
        this.startTime = ko.observable().revertable();
        this.endTime = ko
            .observable()
            .revertable()
            .extend({
                validation: {
                    validator: (val) => {
                        if (this.openState().value) {
                            return val > this.startTime();
                        } else {
                            return true;
                        }
                    },
                    message: 'Closing time must be later than opening time',
                },
            });
        this.errors = ko.validation.group(this);

        this.dayName = ko.pureComputed(() => days[this.day()]);

        // last time is only available as closing time
        this.openingHours = ko.computed(() => hours.slice(0, -1));

        this.closingHours = ko.pureComputed(() =>
            hours.filter(
                (hour) => !this.startTime() || hour > this.startTime(),
            ),
        );

        this.isOpen = ko.computed({
            read: () => this.openState().value,
            write: (value) => {
                for (const val of this.openStates()) {
                    if (val.value === value) {
                        this.openState(val);
                    }
                }
            },
        });
        this.fields = [this.openState, this.startTime, this.endTime];
    }

    load() {
        this.fields.forEach((f) => f.commit());
    }

    save() {
        this.fields.forEach((f) => f.commit());
    }

    cancel() {
        this.fields.forEach((f) => f.revert());
    }

    formatTime(t) {
        return format(t, 'h:mm a');
    }
}

class PhoneNumber {
    constructor(mgr, practice) {
        this._mgr = mgr;
        this._practice = practice;
        this.id = ko.observable();
        this.number = ko
            .observable()
            .revertable()
            .extend({practicePhone: true});
        this.type = ko.observable('phone').revertable();
        this.description = ko.observable('').revertable();
        this.fields = [this.number, this.type, this.description];
        this.errors = ko.validation.group(this);
        this.number.subscribe((phoneNumber) => {
            addToLocalStorage({phoneNumber});
        });
    }

    formatNumber(number) {
        let groups;
        if (!number.length) {
            return null;
        }
        if (number.length === 6) {
            groups = [
                number.substring(0, 2),
                number.substring(2, 4),
                number.substring(4),
            ];
        } else if (
            number.substring(0, 2) === '04' ||
            number.substring(0, 4) === '1300' ||
            number.substring(0, 4) === '1800'
        ) {
            groups = [
                number.substring(0, 4),
                number.substring(4, 7),
                number.substring(7),
            ];
        } else {
            groups = [
                `(${number.substring(0, 2)})`,
                number.substring(2, 6),
                number.substring(6),
            ];
        }
        return groups.join(' ');
    }

    load(data) {
        this.id(data.id);
        this.number(this.formatNumber(data.number));
        this.type(data.type);
        this.description(data.description || '');
        this.fields.forEach((f) => f.commit());
    }

    delete() {
        const url = `${this._practice.url()}phone-numbers/${this.id()}/`;
        const d = new Deferred();
        http.delete({url}).then(() => {
            this.id(null);
            d.resolve();
        }, d.reject);
        return d;
    }

    cancel() {
        this.fields.forEach((f) => {
            f.revert();
        });
        if (!this.number()) {
            return true;
        }
        return false;
    }

    async save() {
        let deleted = false;
        if (this.number()) {
            let url = `${this._practice.url()}phone-numbers/`;
            let method = 'POST';
            if (this.id()) {
                url += `${this.id()}/`;
                method = 'PUT';
            }
            const index = this._practice.phones.indexOf(this);
            const order = index > 0 ? index : 0;
            const data = await http.request({
                method,
                url,
                data: {
                    number: this.number(),
                    type: this.type(),
                    description: this.description(),
                    order,
                },
            });
            this.id(data.id);
        } else {
            // If we don't have a number, but we have an id, delete
            if (this.id()) {
                await this.delete();
            }
            deleted = true;
        }
        this.fields.forEach((f) => f.commit());
        return deleted;
    }
}

export class Practice {
    constructor(manager) {
        this._manager = manager;
        this.id = ko.observable();
        this.notifications = ko.observableArray();
        this.name = ko.observable().revertable();
        this.street1 = ko.observable().revertable();
        this.street2 = ko
            .observable()
            .revertable()
            .extend({
                required: {
                    onlyIf: this.street1,
                    message: 'Add your street address',
                },
                showNotification: true,
                notificationMessage: 'Add your street address',
                notificationElement: 'street',
            });
        this.locality = ko.observable().revertable().extend({required: true});
        this.email = ko
            .observable()
            .revertable()
            .extend({
                email: true,
                showNotification: (email) => {
                    if (email && !this.emailErrors().length) {
                        return false;
                    }
                    return true;
                },
                notificationMessage: 'Add your email',
                notificationElement: 'email',
            });
        this.emailErrors = ko.validation.group(this.email);
        this.referralEmail = ko
            .observable()
            .revertable()
            .extend({email: true});
        this.phones = ko
            .observableArray([new PhoneNumber(manager, this)])
            .extend({
                showNotification: (phones) => {
                    for (const phone of phones) {
                        if (phone.number() && !phone.errors().length) {
                            return false;
                        }
                    }
                    return true;
                },
                notificationMessage: 'Add your phone number',
                notificationElement: 'phone',
            });
        this.phoneSet = ko
            .pureComputed(() => {
                for (const phone of this.phones()) {
                    if (phone.number()) {
                        return true;
                    }
                }
            })
            .extend({
                showNotification: true,
            });
        this.fax = ko
            .observable()
            .extend({
                phone: {
                    message: 'Please enter a valid fax number.',
                },
            })
            .revertable();
        this.faxId = ko.observable();
        this.website = ko
            .observable()
            .revertable()
            .extend({prependHttp: true, url: true});
        this.medicareNumber = ko
            .observable()
            .extend({medicareProvider: true})
            .revertable();
        this.medicareErrors = ko.validation.group(this.medicareNumber);
        this.practiceManagementSystem = ko.observable().revertable().extend({
            showNotification: true,
            notificationMessage: 'Add your practice management system',
            notificationElement: 'system',
        });
        this.offersTelehealth = ko.observable(false).extend({
            showNotification: true,
            notificationMessage:
                'Add whether you offer telehealth services for this practice',
            notificationElement: 'offersTelehealth',
        });
        this.hasHicaps = ko.observable(false);
        this.operationHoursComments = ko.observable().revertable();
        this.hoursOfOperation = ko.observableArray([]).extend({
            showNotification: (hoursOfOperation) => {
                for (const hours of hoursOfOperation) {
                    if (hours.isOpen()) {
                        return false;
                    }
                }
                return true;
            },
            notificationMessage: 'Add your practice hours',
            notificationElement: 'hours',
        });
        this.practiceHoursSet = ko
            .pureComputed(() => {
                for (const day of this.hoursOfOperation()) {
                    if (day.isOpen()) {
                        return true;
                    }
                }
            })
            .extend({
                showNotification: true,
            });
        this.insuranceTypes = ko.observableArray([]);
        this.paymentConcerns = ko.observableArray([]);
        this.paymentTypes = ko.observableArray([]);
        this.serviceOptions = ko.observableArray([]);
        this.twilioPhoneNumber = ko.observable();
        this.fullyLoaded = ko.observable(false);
        this.errors = ko.validation.group(this, {
            deep: true,
            observable: false,
        });
        this.saved = ko.observable(false);
        this.created = ko.observable();
        this.deletionReason = ko.pureComputed(() => {
            if (this.selectedReason() === this.reasonOptions[4]) {
                return this.otherReason();
            } else {
                return this.selectedReason();
            }
        });
        this.otherReason = ko.observable();
        this.deletionRequested = ko.observable(false);
        this.editName = ko.observable(false);
        this.selectedReason = ko.observable();
        this.reasonOptions = [
            'Practice has closed down',
            'Practice has moved locations',
            'I no longer work at this location',
            'I need help to reorder locations on my profile',
            'Other (please specify)',
        ];

        this.modalInstance = null;

        this.insuranceTypeChanges = monitorArray(this.insuranceTypes);
        this.paymentConcernChanges = monitorArray(this.paymentConcerns);
        this.paymentTypeChanges = monitorArray(this.paymentTypes);
        this.serviceOptionChanges = monitorArray(this.serviceOptions);
        this.showAllNotifications = ko.observable();
        this.dashArray = ko.observable(471.238);

        this.notificationFields = [
            this.street2,
            this.phones,
            this.email,
            this.practiceManagementSystem,
            this.hoursOfOperation,
        ];

        this.fields = [
            this.name,
            this.street1,
            this.street2,
            this.locality,
            this.email,
            this.referralEmail,
            this.medicareNumber,
            this.fax,
            this.website,
            this.practiceManagementSystem,
            this.operationHoursComments,
        ];

        this.incompleteFields = ko.pureComputed(() => {
            const notificationLength = this.notifications().length;
            if (notificationLength > 1) {
                return `${notificationLength} fields to update`;
            } else {
                return `${notificationLength} field to update`;
            }
        });

        this.notificationsMessages = ko.pureComputed(() =>
            this.notificationFields
                .filter((n) => n.showNotification(n()))
                .map((n) => ({
                    message: n.notificationMessage,
                    field: n,
                })),
        );

        this.notificationsMessages.subscribe((val) => {
            this.notifications(val);
        });
        this.name.subscribe((val) => {
            addToLocalStorage({practiceName: val});
        });
        this.email.subscribe((val) => {
            addToLocalStorage({email: val});
        });

        this.phones.subscribe((val) => {
            if (!val.length) {
                addToLocalStorage({phoneNumber: null});
                return;
            }
            addToLocalStorage({phoneNumber: val[0].number()});
        });

        this.percent = ko.pureComputed(() => {
            const ns = this.notificationFields;
            return (
                1 -
                ns.filter((n) => n.showNotification(n())).length / ns.length
            );
        });

        this.percentDisplay = ko.pureComputed(() => {
            const roundedPercent = Math.round((this.percent() * 1000) / 10);
            return `${roundedPercent} %`;
        });

        this.radial = ko.pureComputed(
            () => (1 - this.percent()) * this.dashArray(),
        );

        this.url = ko.pureComputed(() => {
            if (this.id()) {
                return `/api/practices/v1/positions/${this.id()}/`;
            }
        });

        this.alertHidden = ko.observable(true);

        this.isValid = ko.pureComputed(() => {
            let valid = true;
            for (const hours of this.hoursOfOperation()) {
                if (hours.errors().length) {
                    valid = false;
                    break;
                }
            }
            return valid && !this.errors().length;
        });

        this.number = ko.pureComputed(
            () => this._manager.practices.indexOf(this) + 1,
        );

        this.displayName = ko.pureComputed(() => {
            if (this.name()) {
                return this.name();
            } else if (this.locality()) {
                return this.locality().name();
            } else {
                return `Practice ${this.number()}`;
            }
        });

        this.mapUrl = ko.pureComputed(() => {
            if (this.locality()) {
                let address = [
                    this.street1(),
                    this.street2(),
                    this.locality().name(),
                    this.locality().state(),
                ]
                    .filter((part) => part)
                    .join(',');
                address = encodeURIComponent(address);
                const googleAPIKey = document.body.dataset.googleApiKey;
                return [
                    'https://maps.googleapis.com/maps/api/staticmap?center=' +
                        address,
                    '&zoom=14&size=640x640&format=png&maptype=roadmap',
                    '&style=element:geometry%7Ccolor:0xf5f5f5',
                    '&style=element:labels.icon%7Cvisibility:off',
                    '&style=element:labels.text.fill%7Ccolor:0x616161',
                    '&style=element:labels.text.stroke%7Ccolor:0xf5f5f5',
                    '&style=feature:administrative%7Celement:geometry%7Cvisibility:off',
                    '&style=feature:administrative.land_parcel%7Cvisibility:off',
                    '&style=feature:administrative.land_parcel%7Celement:labels.text.fill%7Ccolor:0xbdbdbd',
                    '&style=feature:administrative.neighborhood%7Cvisibility:off',
                    '&style=feature:poi%7Cvisibility:off',
                    '&style=feature:poi%7Celement:geometry%7Ccolor:0xeeeeee',
                    '&style=feature:poi%7Celement:labels.text.fill%7Ccolor:0x757575',
                    '&style=feature:poi.park%7Celement:geometry%7Ccolor:0xe5e5e5',
                    '&style=feature:poi.park%7Celement:labels.text.fill%7Ccolor:0x9e9e9e',
                    '&style=feature:road%7Celement:geometry%7Ccolor:0xffffff',
                    '&style=feature:road%7Celement:labels%7Cvisibility:off',
                    '&style=feature:road%7Celement:labels.icon%7Cvisibility:off',
                    '&style=feature:road.arterial%7Celement:labels.text.fill%7Ccolor:0x757575',
                    '&style=feature:road.highway%7Celement:geometry%7Ccolor:0xdadada',
                    '&style=feature:road.highway%7Celement:labels.text.fill%7Ccolor:0x616161',
                    '&style=feature:road.local%7Celement:labels.text.fill%7Ccolor:0x9e9e9e',
                    '&style=feature:transit%7Cvisibility:off',
                    '&style=feature:transit.line%7Celement:geometry%7Ccolor:0xe5e5e5',
                    '&style=feature:transit.station%7Celement:geometry%7Ccolor:0xeeeeee',
                    '&style=feature:water%7Celement:geometry%7Ccolor:0xc9c9c9',
                    '&style=feature:water%7Celement:labels.text%7Cvisibility:off',
                    '&style=feature:water%7Celement:labels.text.fill%7Ccolor:0x9e9e9e',
                    '&maptype=roadmap&key=' + googleAPIKey,
                ].join('');
            }
        });

        this.backgroundCss = ko.pureComputed(() => {
            let css = '#eee';
            if (this.mapUrl()) {
                css += ` url('${this.mapUrl()}') no-repeat center`;
            }
            return css;
        });

        ko.bindingHandlers.stopBubble = {
            init: function (element) {
                ko.utils.registerEventHandler(element, 'click', function (
                    event,
                ) {
                    event.cancelBubble = true;
                    if (event.stopPropagation) {
                        event.stopPropagation();
                    }
                });
            },
        };

        // Populate initial hours
        for (const key of Object.keys(days)) {
            this.hoursOfOperation.push(new PracticeDayHours(key));
        }
        this.addPhone = this.addPhone.bind(this);
    }

    dismissAlert() {
        this.alertHidden(true);
    }

    connectionPMSHandler(data, event) {
        const pms = this.practiceManagementSystem().name();
        page.alert(
            page.alertTypes.SUCCESS,
            `Please complete the ${pms} workflow that was opened in a new tab`,
            6000,
        );
        event.target.classList.add('disabled');
        return true;
    }

    scrollToNotification(notification) {
        const elem = document.getElementById(
            notification.field.notificationElement,
        );
        const elemTop = Math.round(elem.getBoundingClientRect().top);
        const scrollTop =
            document.defaultView.pageYOffset ||
            document.documentElement.scrollTop ||
            document.body.scrollTop;
        const nav = document.getElementsByClassName('menu-bar')[0];
        const navHeight = Math.round(nav.getBoundingClientRect().height);
        window.scrollTo(window.scrollY, elemTop + scrollTop - navHeight);
    }

    fullNotifications() {
        this.showAllNotifications(!this.showAllNotifications());
    }

    requestDeletion() {
        this.modalInstance.show();
    }

    async requestDelete() {
        const url = `/api/practices/v1/positions/${this.id()}/`;
        const data = {
            reason: this.deletionReason(),
        };
        await http.delete({url, data});
        this.deletionRequested(true);
        this.modalInstance.hide();
        page.alert(page.alertTypes.SUCCESS, 'Practice deletion request sent.');
    }

    addPhone() {
        this.phones.push(new PhoneNumber(this._manager, this));
    }

    load(data) {
        this.saved(false);
        this.created(data.created);
        this.id(data.id);
        this.name(data.name);
        this.street1(data.street1);
        this.street2(data.street2);
        const locality = new Locality(this._manager);
        locality.load(data.locality);
        this.locality(locality);
        this.notifications(Object.keys(data.notifications));
        this.deletionRequested(data.deletion_requested);
        this.offersTelehealth(data.offers_telehealth);
        if (!this.modalInstance) {
            this.modalInstance = new bsn.Modal(
                document.getElementById('deletionModal'),
            );
        }
    }

    cancel() {
        this.fields.forEach((f) => f.revert());
        // loop backwards through phone array to ensure
        // that blank phone numbers can be removed except
        // for the first phone number
        for (let i = this.phones().length - 1; i >= 0; i--) {
            const phone = this.phones()[i];
            if (phone.cancel()) {
                if (this.phones().length !== 1) {
                    this.phones().splice(i, 1);
                }
            }
        }
        for (const day of this.hoursOfOperation()) {
            day.cancel();
        }
        this.paymentTypeChanges.revert();
        this.paymentConcernChanges.revert();
        this.alertHidden(true);
    }

    async loadFull() {
        const mainDeferred = new Deferred();
        const deferreds = [mainDeferred];
        this.saved(false);
        http.get({
            url: this.url(),
        }).then((data) => {
            const pms = this._manager
                .practiceManagementSystems()
                .find((obj) => obj.id() === data.practice_management_system);
            this.email(data.email);
            this.referralEmail(data.referral_email);
            this.website(data.website);
            this.medicareNumber(data.medicare_provider_number);
            this.practiceManagementSystem(pms);
            this.operationHoursComments(data.operation_hours_comments);
            this.twilioPhoneNumber(data.twilio_phone_number);
            this.deletionRequested(data.deletion_requested);
            this.offersTelehealth(data.offers_telehealth);
            this.hoursOfOperation([]);
            for (const key of Object.keys(days)) {
                const day = new PracticeDayHours(key);
                day.isOpen(data[`is_open_${key}`]);
                day.startTime(
                    hours.find(
                        (h) =>
                            format(h, 'HH:mm:ss') ===
                            data[`${key}_start_time`],
                    ),
                );
                day.endTime(
                    hours.find(
                        (h) =>
                            format(h, 'HH:mm:ss') === data[`${key}_end_time`],
                    ),
                );
                this.hoursOfOperation.push(day);
                day.load();
            }
            this.fields.forEach((f) => f.commit());
            mainDeferred.resolve();
        });
        deferreds.push(
            this.loadM2M(
                'insurance-types',
                this._manager.insuranceTypes,
                this.insuranceTypes,
                this._manager.deferreds.insuranceTypes,
                this.insuranceTypeChanges,
            ),
        );
        deferreds.push(
            this.loadM2M(
                'payment-concerns',
                this._manager.paymentConcerns,
                this.paymentConcerns,
                this._manager.deferreds.paymentConcerns,
                this.paymentConcernChanges,
            ),
        );
        deferreds.push(
            this.loadM2M(
                'payment-types',
                this._manager.paymentTypes,
                this.paymentTypes,
                this._manager.deferreds.paymentTypes,
                this.paymentTypeChanges,
            ),
        );
        deferreds.push(
            this.loadM2M(
                'service-options',
                this._manager.serviceOptions,
                this.serviceOptions,
                this._manager.deferreds.serviceOptions,
                this.serviceOptionChanges,
            ),
        );

        const numbersLoad = new Deferred();
        deferreds.push(numbersLoad);
        http.get({url: `${this.url()}phone-numbers/`}).then((data) => {
            this.phones([]); // Remove initial entry, add back if needed later
            for (const number of data) {
                // For now, only allow one fax, with no description
                if (number.type === 'fax') {
                    this.fax(number.number);
                    this.faxId(number.id);
                } else {
                    const n = new PhoneNumber(this._manager, this);
                    n.load(number);
                    this.phones.push(n);
                }
            }
            if (!this.phones().length) {
                this.phones.push(new PhoneNumber(this._manager, this));
            }
            numbersLoad.resolve();
        });

        await Promise.all(deferreds);
        this.fullyLoaded(true);
        events.listen(document.body, 'click', () => {
            this.editName(false);
        });
    }

    loadM2M(url, collection, localCollection, deferred, changes) {
        url = `${this.url()}${url}/`;
        const loaded = new Deferred();
        http.get({url}).then((data) => {
            deferred.then(() => {
                const instances = [];
                for (const id of data) {
                    const inst = collection().find((obj) => obj.id() === id);
                    instances.push(inst);
                }
                localCollection(instances);
                // We don't care about 'changes' caused by loading data
                changes.clear();
                loaded.resolve();
            });
        });
        return loaded;
    }

    async save() {
        if (this.errors().length) {
            this.errors.showAllMessages();
            throw new Error('Fix practice field errors');
        }
        const deferreds = [];
        let url = '/api/practices/v1/positions/';
        let method = 'POST';
        if (this.id()) {
            url = `${url}${this.id()}/`;
            method = 'PUT';
        }
        const pms = this.practiceManagementSystem();
        const data = {
            name: this.name(),
            street1: this.street1(),
            street2: this.street2(),
            locality: this.locality().id(),
            email: this.email(),
            referral_email: this.referralEmail(),
            website: this.website(),
            medicare_provider_number: this.medicareNumber(),
            practice_management_system: pms && pms.id(),
            has_hicaps: this.hasHicaps(),
            operation_hours_comments: this.operationHoursComments(),
            source: this._manager.source(),
            offers_telehealth: this.offersTelehealth(),
        };

        for (const day of this.hoursOfOperation()) {
            data[`is_open_${day.day()}`] = day.isOpen() ? 1 : 0;
            const start = day.startTime();
            const end = day.endTime();
            data[`${day.day()}_start_time`] = `${format(start, 'HH:mm')}:00`;
            data[`${day.day()}_end_time`] = `${format(end, 'HH:mm')}:00`;
            day.save();
        }
        const respData = await http.request({method, url, data});
        if (!this.id()) {
            this.id(respData.id);
            url += `${this.id()}/`;
        }
        this.created(respData.created);
        // Save M2Ms once main save has gone through.
        this.insuranceTypeChanges.urlFragment = 'insurance-types/';
        this.paymentConcernChanges.urlFragment = 'payment-concerns/';
        this.paymentTypeChanges.urlFragment = 'payment-types/';
        this.serviceOptionChanges.urlFragment = 'service-options/';
        for (const changes of [
            this.insuranceTypeChanges,
            this.paymentConcernChanges,
            this.paymentTypeChanges,
            this.serviceOptionChanges,
        ]) {
            for (const obj of changes.added()) {
                const data = {id: obj.id()};
                deferreds.push(
                    http.post({
                        url: url + changes.urlFragment,
                        data,
                    }),
                );
            }
            for (const obj of changes.removed()) {
                deferreds.push(
                    http.delete({
                        url: `${url}${changes.urlFragment}${obj.id()}/`,
                    }),
                );
            }
            changes.clear();
        }
        // Save phones
        for (let i = this.phones().length - 1; i >= 0; i--) {
            const phone = this.phones()[i];
            try {
                const d = phone.save();
                deferreds.push(d);
                d.then((deleted) => {
                    if (deleted) {
                        if (this.phones().length !== 1) {
                            this.phones().splice(i, 1);
                        }
                    }
                });
            } catch (ex) {}
        }
        if (this.fax() || this.faxId()) {
            const fax = new PhoneNumber(this._manager, this);
            fax.load({
                id: this.faxId(),
                number: this.fax(),
                type: 'fax',
            });
            deferreds.push(fax.save());
        }

        try {
            await Promise.all(deferreds);
        } catch (ex) {
            throw new Error('Error saving practice');
        }
        const div = document.getElementById('my-practices');
        if (div) {
            div.scrollIntoView(true);
        }
        page.alert(page.alertTypes.SUCCESS, 'Practice Saved.');
        this.fields.forEach((f) => f.commit());
    }

    togglePracticeName() {
        this.editName(!this.editName());
    }

    toString() {
        return `Practice ${this.id()}`;
    }
}

class PracticeManagementSystem {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
        this.hasIntegratedBookings = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        this.hasIntegratedBookings(data.has_integrated_bookings);
    }

    toString() {
        return `Practice Management System: ${this.name()}`;
    }
}

class InsuranceType {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
    }

    toString() {
        return `Insurance Type: ${this.name()}`;
    }
}

class PaymentConcern {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
    }

    toString() {
        return `Payment Concern: ${this.name()}`;
    }
}

class PaymentType {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
    }

    toString() {
        return `Payment Type: ${this.name()}`;
    }
}

class ServiceOption {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
    }

    toString() {
        return `Service Option: ${this.name()}`;
    }
}

export class Locality {
    constructor() {
        this.id = ko.observable();
        this.name = ko.observable();
        this.postCode = ko.observable();
        this.state = ko.observable();
    }

    load(data) {
        this.id(data.id);
        this.name(data.name);
        this.postCode(data.post_code);
        this.state(data.state);
    }

    label() {
        return `${this.name()} ${this.state()} ${this.postCode()}`;
    }

    toString() {
        return `Locality: ${this.name()} ${this.postCode()}`;
    }
}

export class PracticeManager {
    constructor(source) {
        this.source = ko.observable(source);
        this.practices = ko.observableArray();
        this.practiceNotifications = ko.pureComputed(() => {
            let notifications = 0;
            for (const practice of this.practices()) {
                if (
                    practice.notifications().length &&
                    !practice.deletionRequested()
                ) {
                    notifications++;
                }
            }
            return notifications;
        });
        this.incompletePractices = ko.pureComputed(() => {
            const notifications = this.practiceNotifications();
            if (notifications > 1) {
                return `${notifications} Practices are incomplete`;
            } else {
                return `${notifications} Practice is incomplete`;
            }
        });
        this.practiceManagementSystems = ko.observableArray();
        this.insuranceTypes = ko.observableArray();
        this.paymentConcerns = ko.observableArray();
        this.paymentTypes = ko.observableArray();
        this.serviceOptions = ko.observableArray();
        this.selectedPractice = ko.observable();
        this.csrftoken = cookies.get('csrftoken');
        this.ready = ko.observable(true);

        this.selectedPractice.subscribe((practice) => {
            if (!practice) {
                return;
            }
            const practiceData = {
                practiceName: practice.name(),
                email: practice.email(),
                phoneNumber: practice.phoneSet()
                    ? practice.phoneSet()[0].number()
                    : null,
            };
            addToLocalStorage(practiceData);
        });

        this.canAddNew = ko.pureComputed(() => {
            if (this.practices().length >= 6) {
                return false;
            }
            let canAdd = true;
            // Can't add if we've added a new one and not saved it yet
            for (const prac of this.practices()) {
                if (!prac.id()) {
                    canAdd = false;
                    break;
                }
            }
            return canAdd;
        });

        this.deferreds = {
            practice: new Deferred(),
            practiceManagementSystems: new Deferred(),
            insuranceTypes: new Deferred(),
            paymentConcerns: new Deferred(),
            paymentTypes: new Deferred(),
            serviceOptions: new Deferred(),
        };

        this.deferredsList = [];
        for (const key of Object.keys(this.deferreds)) {
            this.deferredsList.push(this.deferreds[key]);
        }

        this.select = this.select.bind(this);
        this.addNew = this.addNew.bind(this);
        this.cancel = this.cancel.bind(this);
        this.save = this.save.bind(this);
    }

    formatTime(item) {
        return format(item, 'HH:mm');
    }

    localities(query, cb) {
        const url = '/api/directory/v1/localities/search/';
        http.get({
            url,
            data: {q: query},
        }).then((data) => {
            cb(
                data.map((val) => {
                    const loc = new Locality();
                    loc.load(val);
                    return {
                        value: loc.id(),
                        text: loc.label(),
                        obj: loc,
                    };
                }),
            );
        });
    }

    loadLocalities(data) {
        const localities = [];
        for (const d of data) {
            const loc = new Locality();
            loc.load(d);
            localities.push(loc);
        }
        return localities;
    }

    async load(loadPractices, bind = true) {
        this.ready(false);
        if (loadPractices) {
            loadData(
                '/api/practices/v1/positions/',
                {},
                this.practices,
                Practice,
                this,
                this.deferreds.practice,
            );
        } else {
            this.deferreds.practice.resolve();
        }
        loadData(
            '/api/practices/v1/practice-management-systems/',
            {},
            this.practiceManagementSystems,
            PracticeManagementSystem,
            this,
            this.deferreds.practiceManagementSystems,
        );
        loadData(
            '/api/practices/v1/insurance-types/',
            {},
            this.insuranceTypes,
            InsuranceType,
            this,
            this.deferreds.insuranceTypes,
        );
        loadData(
            '/api/practices/v1/payment-concerns/',
            {},
            this.paymentConcerns,
            PaymentConcern,
            this,
            this.deferreds.paymentConcerns,
        );
        loadData(
            '/api/practices/v1/payment-types/',
            {},
            this.paymentTypes,
            PaymentType,
            this,
            this.deferreds.paymentTypes,
        );
        loadData(
            '/api/practices/v1/service-options/',
            {},
            this.serviceOptions,
            ServiceOption,
            this,
            this.deferreds.serviceOptions,
        );

        await Promise.all(this.deferredsList);
        if (bind) {
            await events.ready();
            ko.applyBindings(this, document.getElementById('my-practices'));
        }
        this.ready(true);
    }

    cancel() {
        this.selectedPractice().cancel();
        const editing = this.selectedPractice();
        this.selectedPractice(null);
        // If we were editing an unsaved practice, remove it
        if (!editing.id()) {
            this.practices.remove(editing);
        }
        window.scrollTo(0, 0);
    }

    select(practice) {
        practice.saved(false);
        this.selectedPractice(practice);
        if (practice.id() && !practice.fullyLoaded()) {
            practice.loadFull();
        }
        history.pushState({}, document.URL, location.pathname);
        window.onpopstate = () => {
            this.selectedPractice(null);
            window.scrollTo(0, 0);
        };
        window.scrollTo(0, 0);
    }

    addNew() {
        const newPrac = new Practice(this);
        // By definition a new practice doesn't need to load
        newPrac.fullyLoaded(true);
        this.practices.push(newPrac);
        this.select(newPrac);
    }

    save() {
        if (this.selectedPractice().errors().length) {
            this.selectedPractice().alertHidden(false);
        } else {
            this.selectedPractice()
                .save()
                .then(() => {
                    window.scrollTo(0, 0);
                    this.selectedPractice(null);
                });
        }
    }
}
